"use client";

import { useEffect, useState } from "react";
import { cn, scrollToId } from "@/lib/utils";

/* The article element the progress rail measures — stamped in prismic-post. */
const ARTICLE_ID = "post-article";

/* Scroll offset that counts a heading as "reached" (just under the fixed header). */
const SPY_OFFSET = 120;

export interface PostTocItem {
  id: string;
  label: string;
}

interface Props {
  toc: PostTocItem[];
}

/** Sticky right rail of a blog post: a "Jump to" table of contents with a
    vertical reading-progress rail on its left. Items smooth-scroll to their
    heading and highlight as their section is read. Hidden below lg. */
export default function PostSidebar({ toc }: Props) {
  const [progress, setProgress] = useState(0);
  const [active, setActive] = useState("");

  useEffect(() => {
    let raf = 0;

    const update = () => {
      raf = 0;
      const article = document.getElementById(ARTICLE_ID);
      if (article) {
        const rect = article.getBoundingClientRect();
        const total = rect.height - window.innerHeight;
        const scrolled = -rect.top + SPY_OFFSET;
        setProgress(total > 0 ? Math.min(1, Math.max(0, scrolled / total)) : scrolled > 0 ? 1 : 0);
      }
      let current = toc[0]?.id ?? "";
      for (const item of toc) {
        const el = document.getElementById(item.id);
        if (el && el.getBoundingClientRect().top <= SPY_OFFSET) current = item.id;
      }
      setActive(current);
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [toc]);

  return (
    <aside
      className="sticky top-28 hidden max-h-[calc(100vh-8rem)] w-64 shrink-0 self-start overflow-y-auto lg:block"
      aria-label="Article tools"
    >
      <div className="space-y-8">
        {toc.length > 0 && (
          <nav aria-label="Jump to section">
            <p className="text-foreground text-[13px] font-semibold">Jump to</p>
            <div className="relative mt-3">
              {/* Progress rail: full-height track; the foreground-ink fill (black in
                  light, white in dark) grows smoothly as you read */}
              <span aria-hidden="true" className="bg-border absolute inset-y-0 left-0 w-0.5" />
              <span
                aria-hidden="true"
                className="bg-foreground absolute top-0 left-0 w-0.5 transition-[height] duration-300 ease-out"
                style={{ height: `${progress * 100}%` }}
              />
              <ul className="space-y-2.5 pl-4">
                {toc.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToId(item.id);
                      }}
                      className={cn(
                        "block text-[13px] leading-snug transition-colors",
                        active === item.id
                          ? "text-foreground font-medium"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </nav>
        )}
      </div>
    </aside>
  );
}
